import {SectionName, usePutDossierSettings} from '@/entities/dossier-settings';
import {
    Button,
    CheckboxFormField,
    Dialog,
    DialogBody,
    DialogContainer,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    ErrorDisplay,
    Form,
    Helptext,
} from '@/shared/components';
import {SectionDto} from '@/shared/types';
import {zodResolver} from '@hookform/resolvers/zod';
import {Dispatch, SetStateAction} from 'react';
import {useForm, useFormContext, UseFormReturn} from 'react-hook-form';
import z from 'zod';
import {useSectionsContext} from '../../sections-context';

type ConfigurableSection = Exclude<SectionName, "DossierDetails" | "Evaluatie">;

const sectieLabelMap: Record<ConfigurableSection, string> = {
    VsmOfOpstartplan: "VSM/Opstartplan",
    Faciliteiten: "Faciliteiten",
    FaciliteitenLandelijk: "Landelijke faciliteiten",
    Tvv: "Treinvervangend vervoer",
    BetrokkenTreinen: "Betrokken treinen",
    MedewerkersOpStation: "Medewerkers op station",
};

const secties: ConfigurableSection[] = [
    "VsmOfOpstartplan",
    "Faciliteiten",
    "FaciliteitenLandelijk",
    "Tvv",
    "BetrokkenTreinen",
    "MedewerkersOpStation",
];

const updateSectiesSchema = z.object({
    VsmOfOpstartplan: z.boolean(),
    Faciliteiten: z.boolean(),
    FaciliteitenLandelijk: z.boolean(),
    Tvv: z.boolean(),
    BetrokkenTreinen: z.boolean(),
    MedewerkersOpStation: z.boolean(),
});

type UpdateSectiesFormValues = z.infer<typeof updateSectiesSchema>;

type EditSectiesDialogProps = {
    dossierId: string;
    open: boolean;
    onOpenChange: Dispatch<SetStateAction<boolean>>;
};

export function EditSectiesDialog({ dossierId, open, onOpenChange }: EditSectiesDialogProps) {
    const { isSectionEnabled, isSectionInUse } = useSectionsContext();

    const form = useForm<UpdateSectiesFormValues>({
        resolver: zodResolver(updateSectiesSchema),
        defaultValues: {
            VsmOfOpstartplan: isSectionEnabled("VsmOfOpstartplan"),
            Faciliteiten: isSectionEnabled("Faciliteiten"),
            FaciliteitenLandelijk: isSectionEnabled("FaciliteitenLandelijk"),
            Tvv: isSectionEnabled("Tvv"),
            BetrokkenTreinen: isSectionEnabled("BetrokkenTreinen"),
            MedewerkersOpStation: isSectionEnabled("MedewerkersOpStation"),
        },
    });

    const putDossierSettings = usePutDossierSettings();

    const handleSubmit = (values: UpdateSectiesFormValues) => {
        const enabledSections: SectionDto["name"][] = Object.entries(values)
            .filter(([_, checked]) => checked)
            .map(([section]) => section as SectionName);

        putDossierSettings.mutate(
            {
                dossierId,
                enabledSections: [...enabledSections, "DossierDetails", "Evaluatie"], // DossierDetails and Evaluatie should always stay in the list of enabledSections, these can't be toggled manually
            },
            {
                onSuccess: () => onOpenChange(false),
            }
        );
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContainer>
                <DialogHeader>
                    <DialogTitle>Secties instellen</DialogTitle>
                    <DialogDescription className="sr-only">Wijzig secties</DialogDescription>
                </DialogHeader>
                <Form {...form}>
                    <form onSubmit={(event) => void form.handleSubmit(handleSubmit)(event)}>
                        <DialogBody>
                            <fieldset className="mb-1 space-y-1">
                                <legend className="text-content-emphasis text-s font-bold">Kies te tonen secties</legend>
                                <ul className="space-y-1">
                                    {secties.map((sectie) => (
                                        <Sectie key={sectie} sectie={sectie} inGebruik={isSectionInUse(sectie)} />
                                    ))}
                                </ul>
                            </fieldset>
                            <Helptext>
                                (De)selecteren heeft geen invloed op ingezette services of berichten. Secties die in gebruik zijn, kunnen
                                niet uitgezet worden.
                            </Helptext>
                            {putDossierSettings.error && (
                                <div className="mb-4">
                                    <ErrorDisplay error={putDossierSettings.error} variant="inline" />
                                </div>
                            )}
                        </DialogBody>
                        <DialogFooter>
                            <Button type="button" variant="tertiary" onClick={() => onOpenChange(false)}>
                                Annuleren
                            </Button>
                            <Button type="submit" variant="primary" disabled={putDossierSettings.isPending}>
                                Opslaan
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContainer>
        </Dialog>
    );
}

type SectieProps = {
    sectie: ConfigurableSection;
    inGebruik: boolean;
};

function Sectie({ sectie, inGebruik }: SectieProps) {
    const form = useFormContext() as UseFormReturn<UpdateSectiesFormValues>;

    return (
        <li className="flex gap-4">
            <CheckboxFormField form={form} name={sectie} label={sectieLabelMap[sectie]} readOnly={inGebruik} />
            {inGebruik && <p className="text-content-subtle text-s">Bevat data of berichten</p>}
        </li>
    );
}
